import React, { FC, memo, useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { IAccount, IBranch } from '../../types/user'
import useThunkDispatch from '../../common/useThunkDispatch'
import { managerAccountBranchesSelector } from '../managerSelectors'
import ModalWrapper from '../../components/Modal/ModalWrapper'
import DropdownSelect from '../../components/DropdownSelect'
import Input from '../../components/Input'
import Button from '../../components/Button'
import CrossIcon from '../../icons/cross.svg'
import { IProduct } from '../../types/products'
import {
  requestProductTransfer,
  requestBranchesProductsList,
  requestBranchBinLocationItems,
} from './inventoryActions'
import { compact } from 'lodash'
import { money } from '../../common/display'

interface IBranchBinLocationItem {
  locationItemId: number
  locationCode: string
  inventory: number
  userName?: string
}

interface IBinRow extends IBranchBinLocationItem {
  selected: boolean
  amount: number
  selectedAt: number
}

interface IOwnProps {
  opened: boolean
  branch: IBranch
  product: IProduct
  account?: IAccount
  binInventoryFeature?: boolean
  onRefetch: () => void
  onClose: () => void
}

const buildBinRows = (bins: IBranchBinLocationItem[]): IBinRow[] =>
  bins.map((bin) => ({ ...bin, selected: false, amount: 0, selectedAt: 0 }))

const redistributeAmounts = (
  rows: IBinRow[],
  amountRequested: number,
): IBinRow[] => {
  const selected = rows
    .filter((b) => b.selected)
    .sort((a, b) => a.selectedAt - b.selectedAt)

  let remaining = amountRequested
  const amounts: Record<number, number> = {}

  for (const bin of selected) {
    const take = Math.min(bin.inventory, remaining)
    amounts[bin.locationItemId] = take
    remaining -= take
  }

  return rows.map((bin) => ({
    ...bin,
    amount: amounts[bin.locationItemId] ?? 0,
  }))
}

const ProductTransfer: FC<IOwnProps> = ({
  branch: currentBranch,
  opened,
  account,
  product: sourceProduct,
  binInventoryFeature,
  onRefetch,
  onClose,
}) => {
  const dispatch = useThunkDispatch()
  const branches: IBranch[] = useSelector(managerAccountBranchesSelector)
  const [branch, setBranch] = useState<IBranch | undefined>()
  const [targetProduct, setTargetProduct] = useState<IProduct | undefined>(
    sourceProduct,
  )
  const [sourceAmount, setSourceAmount] = useState<number>()
  const [destinationAmount, setDestinationAmount] = useState<number>()
  const [loading, setLoading] = useState<boolean>(false)
  const [binRows, setBinRows] = useState<IBinRow[]>([])

  const isBinEnabled = binInventoryFeature === true

  const getCurrentStock = () => sourceProduct.inventory || 0

  const getSourceAmountError = () => {
    if (sourceAmount === undefined) return undefined
    if (sourceAmount <= 0) return 'Must be greater than 0'
    const stock = getCurrentStock()
    if (sourceAmount > stock) return `Exceeds available stock (${stock})`
    if (isBinEnabled && binRows.some((b) => b.selected)) {
      const selectedTotal = binRows
        .filter((b) => b.selected)
        .reduce((sum, b) => sum + b.inventory, 0)
      if (sourceAmount > selectedTotal)
        return `Selected bins only cover ${selectedTotal}`
    }
    return undefined
  }

  useEffect(() => {
    setBranch(undefined)
    setSourceAmount(undefined)
    setDestinationAmount(undefined)
    setTargetProduct(undefined)
    setBinRows([])
  }, [opened])

  useEffect(() => {
    if (!opened || !isBinEnabled) return

    const fetchBins = async () => {
      const items: IBranchBinLocationItem[] = await dispatch(
        requestBranchBinLocationItems(currentBranch.id, sourceProduct.id),
      )
      let rows = buildBinRows(items.filter((item) => item.inventory > 0))
      if (rows.length === 1) {
        rows = [{ ...rows[0], selected: true, selectedAt: 1 }]
      }
      setBinRows(rows)
    }

    fetchBins()
  }, [opened, isBinEnabled])

  useEffect(() => {
    setBinRows((prev) => redistributeAmounts(prev, sourceAmount ?? 0))
  }, [sourceAmount])

  const handleClose = useCallback(() => {
    onClose()
  }, [])

  const handleChangeAmount = (value: string) => {
    const newValue = parseInt(value, 10)

    setSourceAmount(!!newValue || newValue === 0 ? newValue : undefined)
    if (!targetProduct || targetProduct.unit === sourceProduct.unit) {
      handleChangeDestinationAmount(value)
    }
  }

  const handleChangeDestinationAmount = (value: string) => {
    const newValue = parseInt(value, 10)

    setDestinationAmount(!!newValue || newValue === 0 ? newValue : undefined)
  }

  const toggleBin = (locationItemId: number) => {
    setBinRows((prev) => {
      const maxSelectedAt = Math.max(0, ...prev.map((b) => b.selectedAt))
      const toggled = prev.map((b) => {
        if (b.locationItemId !== locationItemId) return b
        return b.selected
          ? { ...b, selected: false, selectedAt: 0, amount: 0 }
          : { ...b, selected: true, selectedAt: maxSelectedAt + 1 }
      })
      return redistributeAmounts(toggled, sourceAmount ?? 0)
    })
  }

  const handleUpdate = async () => {
    if (branch && sourceAmount && destinationAmount) {
      setLoading(true)
      const selectedInOrder = binRows
        .filter((b) => b.selected)
        .sort((a, b) => a.selectedAt - b.selectedAt)
        .map((b) => b.locationItemId)
      const locationIds =
        selectedInOrder.length > 0 ? selectedInOrder : undefined
      const errors = await dispatch(
        requestProductTransfer(
          sourceProduct.code,
          currentBranch.id,
          branch.id,
          sourceAmount,
          destinationAmount,
          undefined,
          locationIds,
        ),
      )
      if (!errors) {
        onRefetch()
      } else {
        alert(errors.base)
      }
      setLoading(false)
    }
    handleClose()
  }

  useEffect(() => {
    if (!branch) {
      setTargetProduct(undefined)
      return
    }

    const perform = async (branchId: number) => {
      setLoading(true)
      const tProducts = await dispatch(
        requestBranchesProductsList(
          branchId,
          sourceProduct.code,
          undefined,
          account ? account.id : undefined,
        ),
      )
      setTargetProduct(tProducts[0])
      setLoading(false)
    }

    perform(branch.id)
  }, [branch])

  if (!opened) return null

  const options = compact(
    branches.map((branch) =>
      branch.id === currentBranch.id
        ? undefined
        : {
            value: branch.id,
            label: branch.name,
          },
    ),
  )

  return (
    <ModalWrapper className="import">
      <button className="import_backdrop clear-button" onClick={handleClose} />
      <div className="import_dialog transfer-modal -sm">
        <div className="transfer-modal_title">
          Transfer {sourceProduct.name}
        </div>
        <CrossIcon className="transfer-modal_close" onClick={handleClose} />

        <div className="transfer-modal_field">
          <DropdownSelect<number | undefined>
            label="DESTINATION BRANCH"
            className="-md"
            value={branch && branch.id}
            options={options}
            onChange={(id) => {
              setBranch(branches.find((b) => b.id === id))
            }}
            dropdownValueClassName="input_input -flex"
          />
        </div>

        <div className="transfer-modal_field">
          <Input
            label={`SEND AMOUNT (${money(sourceProduct.price ?? 0)} / ${sourceProduct.unit})`}
            value={sourceAmount ?? ''}
            type="number"
            min="1"
            onChange={(e) => handleChangeAmount(e.target.value)}
            autoComplete="off"
            error={getSourceAmountError()}
          />
          <span style={{ color: '#757D9F', fontSize: 13 }}>
            {`Current stock: ${getCurrentStock()} ${getCurrentStock() !== 1 ? 'items' : 'item'}`}
          </span>
        </div>

        {isBinEnabled && binRows.length > 0 && (
          <div className="transfer-modal_field">
            <div className="transfer-modal_bins-title">Select source bins</div>
            {binRows.map((bin) => (
              <div
                key={bin.locationItemId}
                className="transfer-modal_bin-row"
                onClick={() => toggleBin(bin.locationItemId)}
              >
                <button
                  type="button"
                  className={`clear-button transfer-modal_bin-toggle${bin.selected ? ' -on' : ''}`}
                />
                <span className="transfer-modal_bin-label">
                  {bin.locationCode}
                  {bin.userName ? ` · ${bin.userName}` : ''}
                </span>
                <span className="transfer-modal_bin-inventory">
                  {bin.inventory}
                </span>
                <span
                  className="transfer-modal_bin-deduction"
                  style={{
                    visibility:
                      bin.selected && bin.amount > 0 ? 'visible' : 'hidden',
                  }}
                >
                  -{bin.amount}
                </span>
              </div>
            ))}
          </div>
        )}

        {targetProduct && targetProduct.unit !== sourceProduct.unit && (
          <div className="transfer-modal_field -repacking">
            <span className="alert">
              Destination branch has different repackaging settings. Please,
              specify receive amount.
            </span>
            <Input
              label={`RECEIVE AMOUNT (${money(targetProduct.price ?? 0)} / ${targetProduct.unit})`}
              value={destinationAmount ?? ''}
              onChange={(e) => handleChangeDestinationAmount(e.target.value)}
              autoComplete="off"
            />
          </div>
        )}
        <Button
          color="primary"
          type="submit"
          disabled={
            !branch ||
            !sourceAmount ||
            sourceAmount <= 0 ||
            !destinationAmount ||
            destinationAmount <= 0 ||
            !!getSourceAmountError() ||
            (isBinEnabled &&
              binRows.length > 0 &&
              !binRows.some((b) => b.selected))
          }
          loading={loading}
          className="transfer-modal_button"
          onClick={handleUpdate}
        >
          Transfer
        </Button>
        <div className="transfer-modal_cancel" onClick={handleClose}>
          Cancel
        </div>
      </div>
    </ModalWrapper>
  )
}

export default memo(ProductTransfer)
